import { Component, Inject, Pipe, PipeTransform } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CartExceptionEnum,
  IAddToCartErrorDialogData,
  IAddToCartErrorResponse,
  IAddToCartExceptionPayload,
  IAddToCartGroupedError,
  IAddToCartResult,
  IGroupedCartItem,
  INewCartItem,
} from "@gloria-public/order/domain/interfaces/cart.interface";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { Tools } from "@shared/utils/helpers/tools";
import { ITranslation } from "@shared/utils/helpers/translation.interface";
import { catchError, concatMap, from, map, of, toArray } from "rxjs";

@Pipe({
  name: "groupedErrorNames",
  pure: true,
})
export class GroupedErrorNamesPipe implements PipeTransform {
  transform(items: IGroupedCartItem[]): string {
    return items.map((i) => i.name).join(", ");
  }
}

@Component({
  selector: "app-add-to-cart-error",
  templateUrl: "./add-to-cart-error.component.html",
  styleUrls: ["./add-to-cart-error.component.scss"],
})
export class AddToCartErrorComponent {
  public errors: IAddToCartGroupedError[] = [];
  public required?: IAddToCartExceptionPayload;
  public isAddingToCart = false;

  constructor(
    private orderService: OrderService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<AddToCartErrorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAddToCartErrorDialogData
  ) {
    if (data) {
      this.errors = data.errors;
      this.required = data.required;
    }
  }

  public addDna(): void {
    if (!this.required || this.isAddingToCart) {
      return;
    }
    const required = this.required;

    this.isAddingToCart = true;

    const dnaItem: INewCartItem = {
      serviceRequestID: required.dna,
      riskTemplateID: required.risk,
    };

    const dnaError = this.errors.find((e) => e.code === CartExceptionEnum.DNA_NOT_DECODED);

    if (!dnaError) {
      this.isAddingToCart = false;
      return;
    }

    const retryItems = dnaError.items;

    from([dnaItem, ...retryItems.map((i) => i.item)])
      .pipe(
        concatMap((item) => {
          const retryItem = retryItems.find((i) => i.item.serviceRequestID === item.serviceRequestID);

          const dnaName = required.message
            ? Tools.getTranslation(required.message, this.translateService.currentLang as keyof ITranslation, "")
            : required.dna;

          const name = retryItem?.name ?? dnaName;

          return this.orderService.addItemToCart(item, this.translateService.currentLang).pipe(
            map(() => ({
              status: "success" as const,
              name,
              item,
            })),
            catchError((err) => {
              return of({
                status: err?.status === 409 ? ("conflict" as const) : ("error" as const),
                item,
                name,
                error: err?.error as IAddToCartErrorResponse,
              });
            })
          );
        }),
        toArray()
      )
      .subscribe((results: IAddToCartResult[]) => {
        const conflicts = results.filter((r) => r.status === "conflict");
        const errors = results.filter((r) => r.status === "error");
        const successes = results.filter((r) => r.status === "success");

        const conflictsGrouped = Object.values(
          conflicts.reduce(
            (acc, conflict) => {
              if (!conflict.error) return acc;

              const code = conflict.error.error;

              if (!acc[code]) {
                acc[code] = {
                  code,
                  items: [],
                };
              }

              acc[code].items.push({
                item: conflict.item,
                name: conflict.name,
              });

              return acc;
            },
            {} as Record<string, IAddToCartGroupedError>
          )
        );

        this.errors = conflictsGrouped;

        // DNA was added successfully
        if (successes.some((s) => s.item.serviceRequestID === dnaItem.serviceRequestID)) {
          this.required = undefined;
        }

        if (errors.length > 0) {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        }

        if (successes.length > 0) {
          this.snackBar.open(
            this.translateService.instant(
              successes.length > 1 ? "page.questionnaire.completed.success_plural" : "page.questionnaire.completed.success",
              { count: successes.length }
            ),
            "OK",
            { duration: 3000 }
          );
        }

        if (conflicts.length === 0) {
          this.isAddingToCart = false;
          this.close();
          return;
        }

        this.isAddingToCart = false;
      });
  }

  /**
   * Closes the dialog without action
   */
  public close(): void {
    this.dialogRef.close(false);
  }
}
